import * as schemas from "./schemas";
import { getToken } from "~/apps/shared/services/token";
import * as net from "~/lib/net";
import { usePagedQuery } from "~/common/hooks/usePagedQuery";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ProductSegment, ProductSegmentReq } from "../models";

export const getProductCategories = async ({
  offset,
  limit,
}: {
  offset: number;
  limit: number;
}): Promise<ProductSegment[]> => {
  const url = net.buildUrl(`/api/v2/productcategories/`, { offset, limit });
  const data = await net.apiGet(url, getToken());
  return schemas.ProductSegmentListSchema.load(data);
};

export const getProductCategoryById = async (id: string): Promise<ProductSegment> => {
  const data = await net.apiGet(`/api/v2/productcategory/${id}`, getToken());
  return schemas.ProductSegmentSchema.load(data);
};

export const createProductCategory = async (req: ProductSegmentReq) => {
  const payload = schemas.ProductSegmentReqSchema.save(req);
  const data = await net.apiPost("/api/v2/productcategory/", getToken(), payload);
  return schemas.ProductSegmentSchema.load(data);
};

export const editProductCategory = async ({id, ...rest}: ProductSegmentReq) => {
  const payload = schemas.ProductSegmentReqSchema.save(rest);
  const data = await net.apiPut(`/api/v2/productcategory/${id}`, getToken(), payload);
  return schemas.ProductSegmentSchema.load(data);
};

export const deleteProductCategory = async (id: string) => {
  return await net.apiDelete(`/api/v2/productcategory/${id}`, getToken());
};


// Queries

export const useProductCategories = () => {
  return usePagedQuery(["products-categories", "list"], (offset, limit) => getProductCategories({offset, limit}));
};

export const useProductCategoryById = (id: string) => {
  return useQuery({
    queryKey: ["products-categories", id],
    queryFn: () => getProductCategoryById(id),
    enabled: !!id,
  });
};

export const useCreateProductCategory = () => {
  const queryClient = useQueryClient();
  return useMutation<ProductSegment, { message?: string; errorMessage?: string }, ProductSegmentReq>({
    mutationFn: createProductCategory,
    onSuccess: () => {
      queryClient.invalidateQueries(["products-categories"]);
    },
  });
};

export const useEditProductCategory = () => {
  const queryClient = useQueryClient();
  return useMutation<ProductSegment, { message?: string; errorMessage?: string }, ProductSegmentReq>({
    mutationFn: editProductCategory,
    onSuccess: () => {
      queryClient.invalidateQueries(["products-categories"]);
    },
  });
};

export const useDeleteProductCategory = () => {
  const queryClient = useQueryClient();
  return useMutation<any, { message?: string; errorMessage?: string }, string>({
    mutationFn: deleteProductCategory,
    onSuccess: () => {
      queryClient.invalidateQueries(["products-categories"]);
    },
  });
};
